import React, { useEffect, useState } from "react";
import { CardContent } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllTeamsQuery,
  useGetAllAssigneeBySelectedTeamIdQuery,
  useGetEmailSlaReportQuery,
  useLazyGetEmailSLAReportDownloadQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";
import { Loader } from "react-bootstrap-typeahead";
import { AUTH_TOKEN } from "../../../../constants";

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const isCurrentDateRange = (from: string, to: string) => {
  const today = getTodayDateString();
  return from === today && to === today;
};

type ReportMode = "live" | "historical" | null;

const normalizeEmailSlaResults = (data: unknown): any[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.results)) return obj.results;
    if (Array.isArray(obj.data)) return obj.data;
  }
  return [];
};

const mapSlaItemToRow = (item: any, index: number, rowOffset = 0) => ({
  id: rowOffset + index + 1,
  thread_subject: item.thread_subject || "-",
  contact_name: item.contact_name || "-",
  contact_email: item.contact_email || "-",
  team_name: item.team_name || "-",
  assignee_name: item.assignee_name || "-",
  escalation_level: item.escalation_level || "-",
  entity_created_at_formatted: item.entity_created_at_formatted || "-",
  scheduled_breach_time: item.scheduled_breach_time || "-",
  first_response_at: item.first_response_at || "-",
  first_response_time_ms: item.first_response_time || "-",
  non_actionable_marked_by: item.non_actionable_marked_by || "-",
  non_actionable_marked_at: item.non_actionable_marked_at || "-",
  non_actionable_marked_method: item.non_actionable_marked_method || "-",
  is_non_actionable: item.is_non_actionable ?? "-",
});

const EmailSlaReport = () => {
  const navigate = useNavigate();
  const [optionsTeams, setOptionsTeams] = useState<any[]>([]);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [params, setParams] = useState<any>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedTeamsParam, setSelectedTeamsParam] = useState<string[]>([]);
  const [assignneLoading, setAssignneLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [localLoading, setLocalLoading] = useState(false);
  const [filterByOption, setFilterByOption] = useState<any>([
    { label: "Created Date", value: "Created Date" },
    { label: "Updated Date", value: "Updated-date" },
  ]);
  const [selectedFilter, setSelectedFilter] = useState<any>();
  const [optionsAssignee, setOptionsAssignee] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null);
  const { data: teamsData, status: teamsStatus } = useGetAllTeamsQuery({});
  const { data: assigneeData, status: assigneeStatus } =
    useGetAllAssigneeBySelectedTeamIdQuery(selectedTeamId, {
      skip: !selectedTeamId,
    });

  const [emailSlaReportData, setEmailSlaReportData] = useState<any[]>([]);
  const [historicalMeta, setHistoricalMeta] = useState<{
    count: number;
    current_page: number;
    page_size: number;
  } | null>(null);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [reportMode, setReportMode] = useState<ReportMode>(null);
  const [reportFetching, setReportFetching] = useState(false);

  const {
    data: liveEmailSlaReportData,
    isFetching: liveReportFetching,
    status: liveReportStatus,
  } = useGetEmailSlaReportQuery(params, {
    skip: !params || reportMode !== "live",
  });

  const [triggerEmailSlaDownload, { isFetching: downloadLoading }] =
    useLazyGetEmailSLAReportDownloadQuery();

  const reportingBaseUrl =
    process.env.REACT_APP_REPORTING_SERVER_URL || "https://reporting.bluewhirl.org";

  useEffect(() => {
    if (teamsData && teamsData.teams?.length > 0) {
      const transformedOptions = teamsData.teams.map((team: any) => ({
        label: team.name,
        value: team.id,
      }));
      setOptionsTeams(transformedOptions);
    }
  }, [teamsData]);

  useEffect(() => {
    if (!params || reportMode !== "historical") {
      if (reportMode !== "historical") {
        setEmailSlaReportData([]);
        setHistoricalMeta(null);
      }
      return;
    }

    const controller = new AbortController();
    setLocalLoading(true);
    setReportFetching(true);

    const queryParams: Record<string, string | number | boolean> = {
      from_date: params.gte,
      to_date: params.lte,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      download: false,
    };
    if (params.team_id) queryParams.team_id = params.team_id;
    if (params.assignee) queryParams.assignee = params.assignee;

    axios
      .get(`${reportingBaseUrl}/email/sl-report`, {
        params: queryParams,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
        },
      })
      .then((res) => {
        const data = res?.data;
        const results = normalizeEmailSlaResults(data);
        const count =
          data?.total_rows ?? data?.count ?? data?.total ?? results.length;
        const serverPage = data?.current_page ?? data?.page ?? params.page ?? 1;
        const serverPageSize =
          data?.page_size ?? params.page_size ?? paginationModel.pageSize;
        const apiPage = Number(serverPage);
        const pageSizeNum = Number(serverPageSize);
        const rowOffset = (apiPage - 1) * pageSizeNum;

        setHistoricalMeta({
          count: typeof count === "number" ? count : results.length,
          current_page: apiPage,
          page_size: pageSizeNum,
        });
        setEmailSlaReportData(results);
        setDataRows(results.map((item, index) => mapSlaItemToRow(item, index, rowOffset)));
        setPaginationModel({
          page: Math.max(0, apiPage - 1),
          pageSize: pageSizeNum,
        });
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        toast.error(err?.response?.data?.message || "Failed to fetch email SLA report");
        setEmailSlaReportData([]);
        setHistoricalMeta(null);
        setDataRows([]);
      })
      .finally(() => {
        setLocalLoading(false);
        setReportFetching(false);
      });

    return () => controller.abort();
  }, [params, reportingBaseUrl, reportMode]);

  useEffect(() => {
    if (reportMode !== "live") return;
    if (liveEmailSlaReportData === undefined) {
      setLocalLoading(false);
      return;
    }
    const results = normalizeEmailSlaResults(liveEmailSlaReportData);
    setDataRows(results.map((item, index) => mapSlaItemToRow(item, index)));
    setLocalLoading(false);
  }, [liveEmailSlaReportData, reportMode]);

  useEffect(() => {
    if (params && reportMode === "live") {
      setLocalLoading(true);
    }
  }, [params, reportMode]);

  useEffect(() => {
    if (assigneeData) {
      const transformedAssignees = assigneeData.map((assignee: any) => ({
        label: `${assignee.username}`,
        value: assignee.id,
      }));
      setOptionsAssignee(transformedAssignees);
    }
  }, [assigneeData]);

  useEffect(() => {
    const reportBusy =
      reportMode === "live"
        ? liveReportStatus === "pending"
        : reportFetching;
    if (teamsStatus === "pending" || reportBusy) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [teamsStatus, reportFetching, liveReportStatus, reportMode]);

  useEffect(() => {
    if (assigneeStatus === "pending") {
      setAssignneLoading(true);
    } else {
      setAssignneLoading(false);
    }
  }, [assigneeStatus]);


  // Function to convert milliseconds to HH:MM:SS format
  const formatMillisecondsToTime = (ms: number | null): string => {
    if (ms === null || ms === undefined) return "-";
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(seconds).padStart(2, "0")}`;
  };

  const columns: GridColDef[] = [
    {
      field: "thread_subject",
      headerName: "Subject",
      flex: 1,
      minWidth: 150,
      align: "left",
      headerAlign: "center",
    },
    {
      field: "contact_name",
      headerName: "Contact Name",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "contact_email",
      headerName: "Contact Email",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "team_name",
      headerName: "Team",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "assignee_name",
      headerName: "Assignee",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      valueGetter: (value) => value || "-",
    },
    {
      field: "escalation_level",
      headerName: "Escalation Level",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "entity_created_at_formatted",
      headerName: "Created At",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "scheduled_breach_time",
      headerName: "SL Target",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "first_response_at",
      headerName: "First Response",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "first_response_time_ms",
      headerName: "Resolution Time",
      flex: 1,
      minWidth: 250,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "non_actionable_marked_by",
      headerName: "Non-Actionable marked by",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => value || "-",
    },
    {
      field: "non_actionable_marked_at",
      headerName: "Non-Actionable marked at",
      flex: 1,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => value || "-",
    },
    {
      field: "non_actionable_marked_method",
      headerName: "Non-Actionable type",
      flex: 1,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => {
        if (!value) return "-";
        if (value === "manual") return "Manual";
        if (value === "automatic") return "Automatic";
        return value;
      },
    },

    {
      field: "is_non_actionable",
      headerName: "Is Non-Actionable",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => value || "-",
    },
  ];

  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event: any) => {
    setDateTo(event.target.value);
  };
  const handleTeamChange = (event: any) => {
    setSelectedTeamId(event.target.value);
  };

  const handleAssigneeChange = (event: any) => {
    setSelectedAssignee(event.target.value);
  };
  const handleSubmit = () => {
    if (!dateFrom) {
      toast.error('Please select "From Date"');
      return;
    }
    if (!dateTo) {
      toast.error('Please select "To Date"');
      return;
    }

    const isLive = isCurrentDateRange(dateFrom, dateTo);
    if (!isLive && !selectedAssignee) {
      toast.error('Please select "Assignee"');
      return;
    }

    const queryParams: any = {
      gte: dateFrom,
      lte: dateTo,
    };

    if (!isLive) {
      queryParams.page = 1;
      queryParams.page_size = paginationModel.pageSize;
    }

    if (selectedTeamId) {
      queryParams.team_id = selectedTeamId;
    }
    if (selectedAssignee) {
      queryParams.assignee = selectedAssignee;
    }

    setReportMode(isLive ? "live" : "historical");
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setDataRows([]);
    setHistoricalMeta(null);
    setEmailSlaReportData([]);
    setParams(queryParams);
  };

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setReportMode(null);
    setSelectedTeamId(null);
    setSelectedAssignee(null);
    setEmailSlaReportData([]);
    setHistoricalMeta(null);
    setDataRows([]);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    if (!params || reportMode !== "historical") return;
    setLocalLoading(true);
    setParams({
      ...params,
      page: newPaginationModel.page + 1,
      page_size: newPaginationModel.pageSize,
    });
  };

  const handleDownload = async () => {
    if (!params) {
      toast.error("Please apply filters and submit before generating the report.");
      return;
    }

    try {
      await triggerEmailSlaDownload({
        from_date: params.gte,
        to_date: params.lte,
        page: 1,
        page_size: historicalMeta?.count ?? 99999,
        team_id: params.team_id,
        assignee: params.assignee,
      }).unwrap();

      toast.success("Report generation in progress. Download from exported report section once ready");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to queue report generation.");
    }
  };

  const handleFilterOptionChange = (event: any) => {
    setSelectedFilter(event.target.value);
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 120,
      },
    },
  };

  const rows = dataRows;

  const gridLoading =
    reportMode === "live"
      ? localLoading || liveReportFetching
      : localLoading;

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          <button
            className="btn btn-link mb-2"
            onClick={() => {
              navigate("/reports/email/reports-list");
            }}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{
                fontSize: "20px",
                color: "black",
                cursor: "pointer",
              }}
            ></i>
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">
            {" "}
            Email SL Report
          </span>
        </div>

        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  style: { textTransform: "uppercase" },
                  max: new Date().toISOString().split("T")[0],
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  style: { textTransform: "uppercase" },
                  max: new Date().toISOString().split("T")[0],
                },
              }}
              fullWidth
              value={dateTo}
              onChange={handleDateToChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="team-select-label">Select Teams</InputLabel>
              <Select
                labelId="team-select-label"
                id="team-select"
                value={selectedTeamId}
                onChange={handleTeamChange}
                label="Select Teams"
                MenuProps={MenuProps}
              >
                {optionsTeams.map((team) => (
                  <MenuItem key={team.value} value={team.value}>
                    {team.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="w-100 mw-150px">
            {assignneLoading ? (
              <div>
                {" "}
                <Loader />{" "}
              </div>
            ) : (
              <FormControl fullWidth>
                <InputLabel id="Assignee-select-label">Assignee</InputLabel>
                <Select
                  labelId="Assignee-select-label"
                  id="Assignee-select"
                  value={selectedAssignee ?? ""}
                  onChange={handleAssigneeChange}
                  label="Assignee"
                  MenuProps={MenuProps}
                >
                  {optionsAssignee.map(
                    (assignee) =>
                      assignee.label.length > 1 && (
                        <MenuItem key={assignee.value} value={assignee.value}>
                          {assignee.label}
                        </MenuItem>
                      ),
                  )}
                </Select>
              </FormControl>
            )}
          </div>

          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>

            {reportMode === "live" ? (
              <button
                className="btn btn-primary"
                disabled={!params || rows.length === 0}
              >
                <CsvDownloader
                  filename="Email SLA Report"
                  datas={rows}
                  columns={columns.map((col) => ({
                    id: col.field,
                    displayName: col.headerName,
                  }))}
                  separator=","
                  wrapColumnChar={`"`}
                >
                  Download
                </CsvDownloader>
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!params || downloadLoading}
              >
                {downloadLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Download
                  </>
                ) : (
                  "Download"
                )}
              </button>
            )}

            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>
          </div>
        </div>

        <CardContent style={{ width: "100%", minHeight: "400px" }}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              pagination
              {...(reportMode === "historical"
                ? {
                    paginationMode: "server" as const,
                    rowCount: historicalMeta?.count ?? 0,
                  }
                : {})}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={gridLoading}
              disableColumnMenu
              pageSizeOptions={[5, 10, 15, 20, 50]}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": {
                    justifyContent: "flex-end",
                  },
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default EmailSlaReport;
